import React from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './i18n';
import Layout from './components/layout/Layout';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import DocumentList from './pages/DocumentList';
import DocumentEditor from './pages/DocumentEditor';
import DocumentView from './pages/DocumentView';
import Clients from './pages/Clients';
import ClientStatement from './pages/ClientStatement';
import Recurring from './pages/Recurring';
import Items from './pages/Items';
import Settings from './pages/Settings';
import EInvoiceSettings from './pages/EInvoiceSettings';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import PublicDocument from './pages/PublicDocument';
import Landing from './pages/Landing';
import Legal from './pages/Legal';
import Support from './pages/Support';
import SupportCaseView from './pages/SupportCaseView';
import AdminSupportCases from './pages/admin/SupportCases';
import AdminSupportCaseDetail from './pages/admin/SupportCaseDetail';
import AdminStats from './pages/admin/SystemStats';
import AdminUsers from './pages/admin/Users';
import AdminBackups from './pages/admin/Backups';
import AdminActivity from './pages/admin/ActivityLog';
import AdminUserStats from './pages/admin/UserStats';
import AdminChangelog from './pages/admin/Changelog';
import AdminFinance from './pages/admin/Finance';

// Native (Capacitor WebView) loads from capacitor://localhost where there's
// no real URL bar / server-side routing — HashRouter avoids 404s on deep links.
// Web keeps BrowserRouter for clean URLs and SEO.
const AppRouter = Capacitor.isNativePlatform() ? HashRouter : BrowserRouter;

// One shared Layout instance for every signed-in page (layout route with an
// <Outlet/>): navigating between pages — including into /admin/* — must not
// remount Layout, or the sidebar loses its scroll position and refetches.
// Visitors get the public Landing at "/" and a login redirect elsewhere.
function AppShell() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--heading)', fontSize: 18 }}>
      Loading…
    </div>
  );
  if (!user) return pathname === '/' ? <Landing /> : <Navigate to="/login" replace />;
  return <Layout><Outlet /></Layout>;
}

// Nested under AppShell, so the user is already authenticated.
function AdminOnly() {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return <Outlet />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/" replace /> : children;
}

export default function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
      <AuthProvider>
        <AppRouter>
          <Routes>
            <Route path="/login" element={<PublicRoute><Auth /></PublicRoute>} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Share link opened by a user's client — no login, no app layout */}
            <Route path="/d/:token" element={<PublicDocument />} />
            {/* Public legal pages — reachable signed in or out */}
            <Route path="/terms"   element={<Legal doc="terms" />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/refund"  element={<Legal doc="refund" />} />

            <Route element={<AppShell />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/quotations" element={<DocumentList filterType="quotation" />} />
              <Route path="/invoices"   element={<DocumentList filterType="invoice" />} />
              <Route path="/receipts"   element={<DocumentList filterType="receipt" />} />
              <Route path="/bills"      element={<DocumentList filterType="bill" />} />
              <Route path="/purchase-orders" element={<DocumentList filterType="purchase_order" />} />
              <Route path="/delivery-orders" element={<DocumentList filterType="delivery_order" />} />
              <Route path="/credit-notes"    element={<DocumentList filterType="credit_note" />} />
              <Route path="/statements"      element={<DocumentList filterType="statement" />} />
              <Route path="/documents"  element={<DocumentList />} />

              <Route path="/documents/new"      element={<DocumentEditor />} />
              <Route path="/documents/:id"      element={<DocumentView />} />
              <Route path="/documents/:id/edit" element={<DocumentEditor />} />

              <Route path="/recurring"    element={<Recurring />} />
              <Route path="/clients"      element={<Clients />} />
              <Route path="/clients/:id/statement" element={<ClientStatement />} />
              <Route path="/items"        element={<Items />} />
              <Route path="/settings"     element={<Settings />} />
              <Route path="/settings/einvoice" element={<EInvoiceSettings />} />
              <Route path="/profile"      element={<Profile />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/support"      element={<Support />} />
              <Route path="/support/:id"  element={<SupportCaseView />} />

              <Route element={<AdminOnly />}>
                <Route path="/admin/stats"   element={<AdminStats />} />
                <Route path="/admin/users"   element={<AdminUsers />} />
                <Route path="/admin/user-stats" element={<AdminUserStats />} />
                <Route path="/admin/finance"    element={<AdminFinance />} />
                <Route path="/admin/activity"   element={<AdminActivity />} />
                <Route path="/admin/backups" element={<AdminBackups />} />
                <Route path="/admin/changelog" element={<AdminChangelog />} />
                <Route path="/admin/support" element={<AdminSupportCases />} />
                <Route path="/admin/support/:id" element={<AdminSupportCaseDetail />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppRouter>
      </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
